import React, { ChangeEvent, FC, Fragment, KeyboardEvent, useEffect, useState } from 'react';
import { Button, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { useFormikContext } from 'formik';
import { useParams } from 'react-router';
import { QueryResult } from 'react-apollo';

import { useGroupData, useGroupSources, useNotify } from 'hooks';
import { Loaders } from 'components';
import {
  ClientGroupSource,
  ContentApproval,
  GroupSourceType,
  SourceStatus,
  useCreateGroupSourceMutation,
} from 'api';
import { clientGroupSourceCreateResponse } from 'apollo/responses';
import { groupSource } from 'apollo/reducers';

import { getGroupHexColor } from '../../helpers';
import { IGroupForm } from '../../types';
import { UsernameSchema, UrlSchema } from '../../validations';
import Source from './Source';

interface IGroupParams {
  group_id: string;
}

const useStyles = makeStyles(theme => ({
  openIcon: {
    fontSize: theme.typography.pxToRem(32),
  },
  addIcon: {
    color: theme.palette.common.white,
    borderRadius: 4,
    fontSize: theme.typography.pxToRem(12),
  },
  deleteIcon: {
    transform: 'rotate(45deg)',
    color: theme.palette.grey[500],
    marginRight: theme.spacing(2),
  },
  space: {
    marginTop: theme.spacing(4),
  },
}));

const Sources: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notify = useNotify();
  const { groupSources } = useGroupSources();
  const { groupColors } = useGroupData();
  const { values } = useFormikContext<IGroupForm>();
  const { group_id } = useParams<IGroupParams>();

  const hexColor = getGroupHexColor(groupColors, values.color);

  const [inputValue, setInputValue] = useState('');
  const [activeSources, setActiveSources] = useState(null);
  const [inactiveSources, setInactiveSources] = useState(null);

  const [createGroupSourceMutation] = useCreateGroupSourceMutation({
    onError: message => notify.error({ message }),
    onCompleted,
  });

  useEffect(() => {
    setActiveSources(groupSources?.filter(source => source.source_status === SourceStatus.Active));
    setInactiveSources(
      groupSources?.filter(source => source.source_status === SourceStatus.Disabled),
    );
  }, [groupSources]);

  const initialValues = {
    group_id,
    source_status: SourceStatus.Disabled,
    source_type: GroupSourceType.Keyword,
    approval: ContentApproval.Submitted,
    source_value: '',
  };

  function onChangeInput(event: ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    setInputValue(event.target.value);
  }

  function onClickSubmit() {
    const trimmedInputValue = inputValue.trim();
    const isUrl = UrlSchema.isValidSync(trimmedInputValue);
    const isUsername = UsernameSchema.isValidSync(trimmedInputValue);
    let sourceType = GroupSourceType.Keyword;

    if (isUrl) {
      sourceType = GroupSourceType.Rss;
    } else if (isUsername) {
      sourceType = GroupSourceType.TwitterHandle;
    }

    setInputValue('');

    const variables = {
      ...initialValues,
      source_type: sourceType,
      source_value: trimmedInputValue,
    };

    const optimisticResponse = clientGroupSourceCreateResponse(variables);

    return createGroupSourceMutation({
      variables,
      optimisticResponse,
      update: (cache, result) =>
        groupSource({
          cache,
          result,
          name: 'createGroupSource',
          variables: { group_id },
        }),
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      if (!inputValue) {
        return null;
      }

      return onClickSubmit();
    }

    return null;
  }

  function onCompleted(data: QueryResult['data']) {
    const message = t('groups:createSourceSuccess', {
      value: data.clientGroupSourceCreate.source_value,
      group: values.title,
    });

    notify.info({ message });
    setInputValue('');
  }

  function renderSource(source: ClientGroupSource) {
    return (
      <Grid item xs={12} key={source.id}>
        <Source source={source} />
      </Grid>
    );
  }

  function renderEndAdornment() {
    if (!inputValue) {
      return (
        <AddCircleIcon className={classes.openIcon} color="primary" style={{ color: hexColor }} />
      );
    }

    return (
      <Fragment>
        <IconButton onClick={() => setInputValue('')}>
          <AddCircleIcon className={classes.deleteIcon} />
        </IconButton>

        <Button
          variant="contained"
          disabled={!inputValue}
          className={classes.addIcon}
          onClick={onClickSubmit}
          style={{ backgroundColor: hexColor }}>
          {t('common:add')}
        </Button>
      </Fragment>
    );
  }

  if (!groupSources) {
    return <Loaders.Standard />;
  }

  return (
    <Fragment>
      <TextField
        value={inputValue}
        fullWidth
        variant="outlined"
        placeholder={t('groups:sourcesInput')}
        onChange={onChangeInput}
        onKeyDown={onKeyDown}
        InputProps={{
          endAdornment: renderEndAdornment(),
        }}
      />

      {!isEmpty(activeSources) && (
        <Typography
          color="textSecondary"
          variant="subtitle1"
          gutterBottom
          className={classes.space}>
          {t('groups:sourcesActive')}
        </Typography>
      )}
      <Grid container spacing={2}>
        {activeSources?.map(renderSource)}
      </Grid>

      {!isEmpty(inactiveSources) && (
        <Typography
          color="textSecondary"
          variant="subtitle1"
          gutterBottom
          className={classes.space}>
          {t('groups:sourcesInactive')}
        </Typography>
      )}
      <Grid container spacing={2}>
        {inactiveSources?.map(renderSource)}
      </Grid>
    </Fragment>
  );
};

export default Sources;
